import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { getToken } from "@/lib/api";

const WSContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_BASE = BACKEND_URL.replace(/^http/, "ws") + "/api/ws";

export const WSProvider = ({ children }) => {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const heartbeatRef = useRef(null);
  const [connected, setConnected] = useState(false);
  // listeners: Set of callbacks
  const listenersRef = useRef(new Set());

  const subscribe = useCallback((cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  const emit = (payload) => {
    for (const cb of listenersRef.current) {
      try {
        cb(payload);
      } catch {
        /* ignore */
      }
    }
  };

  useEffect(() => {
    if (!user) {
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
        wsRef.current = null;
      }
      setConnected(false);
      return;
    }

    let cancelled = false;
    let failureCount = 0;

    const connect = () => {
      if (cancelled) return;
      const token = getToken();
      if (!token) {
        // No usable token — WS auth will fail. Skip until AuthContext
        // captures one (auth/me echoes it back).
        return;
      }
      const url = `${WS_BASE}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        failureCount = 0;
        // heartbeat every 25 s to keep the connection alive through proxies
        heartbeatRef.current = setInterval(() => {
          try { ws.send("ping"); } catch { /* ignore */ }
        }, 25000);
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          emit(data);
        } catch {
          /* ignore non-json frames */
        }
      };

      ws.onclose = (evt) => {
        setConnected(false);
        clearInterval(heartbeatRef.current);
        if (cancelled) return;
        // Auth-related close codes — don't hammer the server.
        // 4401 = we sent it, 1008 = policy violation, plus HTTP 401/403
        // manifest as close code 1006 (abnormal). We rely on failureCount
        // to catch abnormal-close spam.
        const isAuthClose = evt.code === 4401 || evt.code === 1008;
        if (isAuthClose) return;
        failureCount += 1;
        if (failureCount > 6) return;  // give up after ~6 failed reconnects
        // Exponential backoff, capped at 30s
        const delay = Math.min(30000, 1500 * Math.pow(2, failureCount - 1));
        reconnectRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectRef.current);
      clearInterval(heartbeatRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
        wsRef.current = null;
      }
      setConnected(false);
    };
  }, [user]);

  return (
    <WSContext.Provider value={{ connected, subscribe }}>
      {children}
    </WSContext.Provider>
  );
};

export const useWS = () => useContext(WSContext) || { connected: false, subscribe: () => () => {} };
